/**
 * Takeoff pricing engine — a line-for-line JS mirror of backend
 * routes/takeoff.py's compute_estimate, using the same grids extracted from
 * the estimator's live app. The tab uses this for live totals; the server
 * uses the Python copy when it builds the Excel export, so both always
 * agree.
 */

export const PVC_GRID = {
  '1/2"': { '1"': 11.23, '1.5"': 13.39, '2"': 17.84, '2.5"': 0 },
  '3/4"': { '1"': 11.44, '1.5"': 13.4, '2"': 18.05, '2.5"': 0 },
  '1"': { '1"': 11.75, '1.5"': 15.86, '2"': 20.49, '2.5"': 22.23 },
  '1-1/4"': { '1"': 11.96, '1.5"': 16.29, '2"': 20.88, '2.5"': 22.52 },
  '1.5"': { '1"': 12.27, '1.5"': 16.5, '2"': 21.71, '2.5"': 23.45 },
  '2"': { '1"': 14.65, '1.5"': 19.05, '2"': 22.13, '2.5"': 23.88 },
  '2.5"': { '1"': 15.15, '1.5"': 19.96, '2"': 23.24, '2.5"': 25.84 },
  '3"': { '1"': 17.42, '1.5"': 20.24, '2"': 23.76, '2.5"': 30.08 },
  '4"': { '1"': 19.19, '1.5"': 21.57, '2"': 29.54, '2.5"': 32.19 },
  '5"': { '1"': 20.35, '1.5"': 26.66, '2"': 31.53, '2.5"': 35.76 },
  '6"': { '1"': 24.94, '1.5"': 27.54, '2"': 32.5, '2.5"': 38.9 },
  '7"': { '1"': 26.55, '1.5"': 29.04, '2"': 34.74, '2.5"': 39.69 },
  '8"': { '1"': 29.85, '1.5"': 31.94, '2"': 36.39, '2.5"': 47.95 },
  '9"': { '1"': 30.88, '1.5"': 33.1, '2"': 45, '2.5"': 50.37 },
  '10"': { '1"': 31.6, '1.5"': 40.62, '2"': 47.07, '2.5"': 52.85 },
  '12"': { '1"': 40.19, '1.5"': 43.41, '2"': 50.33, '2.5"': 58.87 },
  '14"': { '1"': 43.06, '1.5"': 46.61, '2"': 56.74, '2.5"': 64.34 },
  '16"': { '1"': 47.81, '1.5"': 52.27, '2"': 61.07, '2.5"': 63.08 },
};

export const SUPPORT_GRID = {
  '1/2"': { '1"': 7.4, '1.5"': 8.88, '2"': 10.66 },
  '3/4"': { '1"': 7.4, '1.5"': 8.88, '2"': 10.66 },
  '1"': { '1"': 8.27, '1.5"': 9.75, '2"': 11.54 },
  '1-1/4"': { '1"': 8.27, '1.5"': 9.75, '2"': 11.54 },
  '1-1/2"': { '1"': 10.06, '1.5"': 11.54, '2"': 11.54 },
  '2"': { '1"': 15.67, '1.5"': 17.45, '2"': 18.93 },
  '2.5"': { '1"': 15.67, '1.5"': 17.45, '2"': 18.93 },
  '3"': { '1"': 17.45, '1.5"': 19.84, '2"': 20.71 },
  '3.5"': { '1"': 17.45, '1.5"': 19.84, '2"': 20.71 },
  '4"': { '1"': 23.67, '1.5"': 26.63, '2"': 28.41 },
  '4.5"': { '1"': 23.67, '1.5"': 26.63, '2"': 28.62 },
  '5"': { '1"': 26.63, '1.5"': 30.19, '2"': 31.18 },
  '6"': { '1"': 30.19, '1.5"': 34.33, '2"': 38.47 },
  '7"': { '1"': 32.55, '1.5"': 36.69, '2"': 40.25 },
  '8"': { '1"': 35.57, '1.5"': 38.47, '2"': 42.03 },
  '9"': { '1"': 0, '1.5"': 0, '2"': 0 },
  '10"': { '1"': 65.44, '1.5"': 78.95, '2"': 95.48 },
  '12"': { '1"': 94.01, '1.5"': 94.01, '2"': 112.92 },
  '14"': { '1"': 108.09, '1.5"': 108.09, '2"': 130.36 },
  '16"': { '1"': 125.2, '1.5"': 125.2, '2"': 147.42 },
};

export const SUPPORT_SPACING_FT = 7;
export const DUCT_INSULATION_RATE = 5;
export const DUCT_VENTURE_CLAD_RATE = 15;

export const PIPE_SYSTEMS = [
  { name: "Cold Water", slug: "cold-water", color: "#2563eb" },
  { name: "Hot Water", slug: "hot-water", color: "#ef4444" },
  { name: "Chilled Water", slug: "chilled-water", color: "#22d3ee" },
  { name: "Heating", slug: "heating", color: "#d946ef" },
  { name: "Misc Heating", slug: "misc-heating", color: "#fb7185" },
  { name: "Condenser Water", slug: "condenser-water", color: "#1d4ed8" },
  { name: "Heat Pump Piping", slug: "heat-pump-piping", color: "#16a34a" },
  { name: "Heat Trace Piping", slug: "heat-trace-piping", color: "#eab308" },
  { name: "Sanitary Piping", slug: "sanitary-piping", color: "#92400e" },
  { name: "Storm", slug: "storm", color: "#8b5cf6" },
  { name: "Condensate", slug: "condensate", color: "#16a34a" },
  { name: "Other", slug: "other", color: "#64748b" },
];

export const SIZES = ['1/2"', '3/4"', '1"', '1-1/4"', '1.5"', '2"', '2.5"', '3"', '4"', '5"', '6"', '7"', '8"', '9"', '10"', '12"', '14"', '16"'];
export const THICKNESSES = ['1"', '1.5"', '2"', '2.5"'];
export const FIXED_LABELS = ["PVC", "Aluminum", "Venture Clad", "Riser"];

// Standard drawing scales. `ratio` = real inches per drawing inch (1/4"=1'
// -> 48). Scale factor stored per page = feet per base-zoom pixel.
export const STANDARD_SCALES = [
  { value: "imperial-3", label: `3" = 1'`, ratio: 4, group: "Imperial" },
  { value: "imperial-1-1/2", label: `1-1/2" = 1'`, ratio: 8, group: "Imperial" },
  { value: "imperial-1", label: `1" = 1'`, ratio: 12, group: "Imperial" },
  { value: "imperial-3/4", label: `3/4" = 1'`, ratio: 16, group: "Imperial" },
  { value: "imperial-1/2", label: `1/2" = 1'`, ratio: 24, group: "Imperial" },
  { value: "imperial-3/8", label: `3/8" = 1'`, ratio: 32, group: "Imperial" },
  { value: "imperial-1/4", label: `1/4" = 1'`, ratio: 48, group: "Imperial" },
  { value: "imperial-3/16", label: `3/16" = 1'`, ratio: 64, group: "Imperial" },
  { value: "imperial-1/8", label: `1/8" = 1'`, ratio: 96, group: "Imperial" },
  { value: "imperial-3/32", label: `3/32" = 1'`, ratio: 128, group: "Imperial" },
  { value: "imperial-1/16", label: `1/16" = 1'`, ratio: 192, group: "Imperial" },
  { value: "imperial-1/32", label: `1/32" = 1'`, ratio: 384, group: "Imperial" },
  { value: "metric-10", label: "1:10", ratio: 10, group: "Metric" },
  { value: "metric-20", label: "1:20", ratio: 20, group: "Metric" },
  { value: "metric-25", label: "1:25", ratio: 25, group: "Metric" },
  { value: "metric-30", label: "1:30", ratio: 30, group: "Metric" },
  { value: "metric-40", label: "1:40", ratio: 40, group: "Metric" },
  { value: "metric-50", label: "1:50", ratio: 50, group: "Metric" },
  { value: "metric-75", label: "1:75", ratio: 75, group: "Metric" },
  { value: "metric-100", label: "1:100", ratio: 100, group: "Metric" },
  { value: "metric-125", label: "1:125", ratio: 125, group: "Metric" },
  { value: "metric-150", label: "1:150", ratio: 150, group: "Metric" },
  { value: "metric-200", label: "1:200", ratio: 200, group: "Metric" },
  { value: "metric-250", label: "1:250", ratio: 250, group: "Metric" },
  { value: "metric-300", label: "1:300", ratio: 300, group: "Metric" },
  { value: "metric-500", label: "1:500", ratio: 500, group: "Metric" },
];

const SIZE_THICKNESS_RE = /^([\d./-]+)"\s+.+?\s+([\d.]+)"\s+INS\s*$/i;

export const parseSizeThickness = (name) => {
  const m = SIZE_THICKNESS_RE.exec((name || "").replace(/[“”]/g, '"').trim());
  return m ? [m[1] + '"', m[2] + '"'] : [null, null];
};

const multiplier = (it) => Math.max(1, Math.round(Number(it.multiplier) || 1));

/** Aluminum jacket = 3 × the matching PVC $/ft (same pipe size + insulation
 * thickness) — Oct-27 running list. Mirrored in backend `_item_rate`. */
export const ALUMINUM_X_PVC = 3;

/** PVC-jacketed $/ft for a linear pipe item: the job's override first, then
 * the company grid; null when the size/thickness has no PVC price. */
const pvcRate = (it, size, thick, pvcOverrides) => {
  const key = `${it.tagId || it.name}|${size}|${thick}`;
  if (pvcOverrides[key] != null) return pvcOverrides[key];
  const g = PVC_GRID[size];
  return g && g[thick] != null ? g[thick] : null;
};

const effectiveRate = (it, pvcOverrides = {}) => {
  const labels = it.labels || [];
  const [size, thick] = parseSizeThickness(it.name);
  if (it.type === "linear" && size && thick) {
    if (labels.includes("Aluminum")) {
      const pvc = pvcRate(it, size, thick, pvcOverrides);
      if (pvc != null) return Math.round(pvc * ALUMINUM_X_PVC * 100) / 100;
    } else if (labels.includes("PVC")) {
      const pvc = pvcRate(it, size, thick, pvcOverrides);
      if (pvc != null) return pvc;
    }
  }
  return it.rate || 0;
};

/** Perimeter (ft) of a duct spec per linear foot: rect {w,h} or round {d}.
 * The spec's own `unit` decides the divisor — mm dims divide by 304.8,
 * inches by 12 (Oct-14 fix: mm ducts were priced 25.4x too high everywhere
 * but the editor preview, which pre-converted). Specs saved before the unit
 * field existed have no `unit` and correctly fall through to inches. */
export const ductPerimeterFt = (spec) => {
  if (!spec) return 0;
  const div = spec.unit === "mm" ? 304.8 : 12;
  if (spec.round || spec.d != null) return (Math.PI * Number(spec.d ?? spec.round)) / div;
  return (2 * (Number(spec.w || 0) + Number(spec.h || 0))) / div;
};

/** Effective square footage for a duct takeoff. */
export const ductSqFt = (it) => {
  if (it.ductSpec) return (it.quantity || 0) * ductPerimeterFt(it.ductSpec);
  return it.quantity || 0; // area tool already measured sq ft
};

export const lineValue = (it, overrides = {}) => {
  const labels = it.labels || [];
  const priceTo = it.priceTo || "";
  // quick items with an explicit destination always price at their OWN rate
  if (priceTo === "duct" || priceTo === "supports" || priceTo.startsWith("extra:")) {
    return (it.quantity || 0) * multiplier(it) * (it.rate || 0);
  }
  const isDuct = it.category === "Duct" || !!it.ductSpec || it.type === "area";
  if (isDuct) {
    const clad = labels.includes("Venture Clad") || /fire/i.test(it.name || "");
    const key = `duct|${it.tagId || it.name}|${clad ? "venture-clad" : "insulation"}`;
    const rate = overrides.duct?.[key] ?? (clad ? DUCT_VENTURE_CLAD_RATE : DUCT_INSULATION_RATE);
    return ductSqFt(it) * multiplier(it) * rate;
  }
  return (it.quantity || 0) * multiplier(it) * effectiveRate(it, overrides.pvc || {});
};

/**
 * Full estimate from a project's takeoff document. Returns grouped lines,
 * section subtotals, and the auto scope text — mirrors the live app.
 */
export function computeEstimate(items, manualItems = [], overrides = {}) {
  const groups = {};
  const supportFeet = {};
  for (const it of items || []) {
    const mult = multiplier(it);
    const labels = [...new Set(it.labels || [])].sort();
    const priceTo = it.priceTo || "";
    if (priceTo.startsWith("extra:")) continue; // priced inside that extra — never in the base
    if (priceTo === "duct" || priceTo === "supports") {
      // quick item with an explicit destination: its OWN rate, routed to
      // Duct Insulation or Rigid Supports (never the duct/PVC grids)
      const section = priceTo === "duct" ? "Duct Insulation" : "Rigid Supports";
      const gkey = `quick|${priceTo}|${it.tagId || `${it.name}|${it.unit}|${it.rate}`}|${JSON.stringify(labels)}`;
      if (groups[gkey]) groups[gkey].quantity += (it.quantity || 0) * mult;
      else groups[gkey] = { section, description: it.name, quantity: (it.quantity || 0) * mult, unit: it.unit || "ea", rate: it.rate || 0, labels, color: it.color, tagId: it.tagId, itemName: it.name, itemUnit: it.unit, itemRate: it.rate };
      continue;
    }
    const isDuct = it.category === "Duct" || !!it.ductSpec || it.type === "area";
    if (isDuct) {
      const clad = (it.labels || []).includes("Venture Clad") || /fire/i.test(it.name || "");
      const key = `duct|${it.tagId || it.name}|${clad ? "venture-clad" : "insulation"}`;
      const rate = overrides.duct?.[key] ?? (clad ? DUCT_VENTURE_CLAD_RATE : DUCT_INSULATION_RATE);
      const gkey = `${it.tagId || `${it.name}|sq ft|${rate}`}|${JSON.stringify(labels)}`;
      if (groups[gkey]) groups[gkey].quantity += ductSqFt(it) * mult;
      else groups[gkey] = { section: "Duct Insulation", description: it.name, quantity: ductSqFt(it) * mult, unit: "sq ft", rate, labels, color: it.color, ductKey: key, tagId: it.tagId, itemName: it.name, itemUnit: it.unit };
    } else {
      const rate = effectiveRate(it, overrides.pvc || {});
      const gkey = `${it.tagId || `${it.name}|${it.unit}|${it.rate}`}|${JSON.stringify(labels)}`;
      if (groups[gkey]) groups[gkey].quantity += (it.quantity || 0) * mult;
      else {
        const [size, thick] = parseSizeThickness(it.name);
        const pvcKey = (it.labels || []).includes("PVC") && size && thick ? `${it.tagId || it.name}|${size}|${thick}` : null;
        groups[gkey] = { section: "Pipe Insulation", description: it.name, quantity: (it.quantity || 0) * mult, unit: it.unit || "ft", rate, labels, color: it.color, pvcKey, tagId: it.tagId, itemName: it.name, itemUnit: it.unit, itemRate: it.rate };
      }
      if (it.type === "linear" && !(it.labels || []).includes("Riser")) {
        const [size, thick] = parseSizeThickness(it.name);
        if (size && thick && SUPPORT_GRID[size]?.[thick] != null) {
          const k = `${size}|${thick}`;
          supportFeet[k] = (supportFeet[k] || 0) + (it.quantity || 0) * mult;
        }
      }
    }
  }
  const supportLines = Object.entries(supportFeet)
    .map(([k, feet]) => {
      const [size, thick] = k.split("|");
      const rate = overrides.support?.[k] ?? SUPPORT_GRID[size][thick];
      return {
        section: "Rigid Supports", description: `Rigid supports — ${size} pipe / ${thick} insulation`,
        quantity: Math.ceil(feet / SUPPORT_SPACING_FT), unit: "ea", rate, labels: [], color: "#64748b", supportKey: k,
      };
    })
    .filter((l) => l.rate > 0)
    .sort((a, b) => a.description.localeCompare(b.description));
  const manualLines = (manualItems || []).map((m) => ({
    section: m.section || "Pipe Insulation", description: m.description, quantity: Number(m.quantity) || 0,
    unit: m.unit || "ea", rate: Number(m.rate) || 0, labels: m.label ? [m.label] : [], color: null, manualId: m.id,
  })).sort((a, b) => a.description.localeCompare(b.description));
  const measured = Object.values(groups).sort((a, b) => a.description.localeCompare(b.description));
  const lines = [...measured, ...supportLines, ...manualLines];
  const by = (s) => lines.filter((l) => l.section === s);
  const tot = (rows) => rows.reduce((s, l) => s + l.quantity * l.rate, 0);
  const pipe = by("Pipe Insulation"), duct = by("Duct Insulation"), sup = by("Rigid Supports");
  return {
    lines, pipeLines: pipe, ductLines: duct, supportLines: sup,
    pipeTotal: tot(pipe), ductTotal: tot(duct), supportTotal: tot(sup),
    total: tot(lines),
    supportCount: supportLines.reduce((s, l) => s + l.quantity, 0),
  };
}

/** The auto scope-of-work paragraph (editable per job in the Estimate view). */
export function defaultEstimateDescription(items) {
  const pipeItems = (items || []).filter((it) => !(it.category === "Duct" || !!it.ductSpec || it.type === "area"));
  const ductItems = (items || []).filter((it) => it.category === "Duct" || !!it.ductSpec || it.type === "area");
  const systems = [...new Set(pipeItems.map((it) => {
    const m = /^[\d./-]+"\s+(.+?)\s+[\d.]+"\s+INS/i.exec((it.name || "").replace(/[“”]/g, '"').trim());
    return m ? m[1] : null;
  }).filter(Boolean))].map((s) => s.toLowerCase());
  const paras = [];
  if (pipeItems.length) {
    const hasPvc = pipeItems.some((it) => (it.labels || []).includes("PVC"));
    const hasAl = pipeItems.some((it) => (it.labels || []).includes("Aluminum"));
    const jacket = hasPvc && hasAl ? "PVC and aluminum jacketing as tagged" : hasAl ? "aluminum jacketing" : "PVC jacketing";
    paras.push(systems.length
      ? `Supply and install fiberglass insulation with ${jacket} on all exposed ${systems.join(" and ")} as shown on the drawings.`
      : `Supply and install fiberglass insulation with ${jacket} on all measured piping and equipment as shown on the drawings.`);
  }
  if (ductItems.length) {
    const fire = ductItems.some((it) => /fire/i.test(it.name || ""));
    const vc = ductItems.some((it) => (it.labels || []).includes("Venture Clad"));
    paras.push(fire
      ? "Supply and install measured duct insulation and fire wrap as shown on the drawings."
      : vc
        ? "Supply and install insulation on all measured ductwork, with Venture Clad jacketing where tagged, as shown on the drawings."
        : "Supply and install insulation on all measured ductwork as shown on the drawings.");
  }
  const hasSupports = pipeItems.some((it) => it.type === "linear" && !(it.labels || []).includes("Riser"));
  if (hasSupports) paras.push(`Provide rigid insulation supports for measured horizontal piping at ${SUPPORT_SPACING_FT} ft centres; risers are excluded.`);
  if (!paras.length) paras.push("Supply and install fiberglass insulation with PVC jacketing on all exposed piping as shown on the drawings.");
  return paras.join("\n\n") + "\n\nNot Included: Metal saddles beneath pipe insulation, scissor lifts, or any specialized equipment required for hard-to-reach areas.";
}

// ---------------- geometry (base-zoom pixel space) ----------------

export const polyLen = (pts) => {
  let s = 0;
  for (let i = 1; i < pts.length; i++) s += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  return s;
};

export const polyArea = (pts) => {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
};

export const money = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtQty = (n) => {
  const v = Number(n) || 0;
  return v % 1 === 0 ? String(v) : v.toFixed(2);
};
